import { z } from 'zod'

// ---------- Threshold Filter ----------
export const ThresholdConditionSchema = z.enum([
  'above',
  'below',
  'between',
  'stability',
])
export type ThresholdCondition = z.infer<typeof ThresholdConditionSchema>

export const ThresholdFilterConfigSchema = z
  .object({
    type: z.literal('threshold_filter'),
    sensor_id: z.string(),
    enabled: z.boolean(),
    condition: ThresholdConditionSchema,
    value_1: z.number(),
    value_2: z.number().nullish(),
  })
  .superRefine((obj, ctx) => {
    if (obj.condition === 'between') {
      if (obj.value_2 === null || obj.value_2 === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: "value2 is required when condition is 'between'",
          path: ['value2'],
        })
      } else if (obj.value_2 < obj.value_1) {
        ctx.addIssue({
          code: 'custom',
          message: "value2 must be ≥ value1 for 'between'",
          path: ['value2'],
        })
      }
    }
  })
  .transform((schema) => ({
    type: schema.type,
    sensorId: schema.sensor_id,
    enabled: schema.enabled,
    condition: schema.condition,
    value1: schema.value_1,
    value2: schema.value_2,
  }))

// ---------- Smoothing ----------
export const SmoothingMethodSchema = z.enum(['avg', 'max', 'min', 'median'])
export type SmoothingMethod = z.infer<typeof SmoothingMethodSchema>

export const SmoothingBasisSchema = z.enum(['samples', 'duration'])
export type SmoothingBasis = z.infer<typeof SmoothingBasisSchema>

export const SmoothingAlignmentSchema = z.enum([
  'trailing',
  'trailingShifted',
  'centered',
])
export type SmoothingAlignment = z.infer<typeof SmoothingAlignmentSchema>

export const SmoothingDisplaySchema = z.enum(['overlay', 'replace'])
export type SmoothingDisplay = z.infer<typeof SmoothingDisplaySchema>

// Declared rather than inferred so the fields below `method` can stay optional
// for call sites that predate them.
export interface SmoothingConfig {
  type: 'smoothing'
  sensorId: string
  enabled: boolean
  windowSize: number
  method: SmoothingMethod
  basis?: SmoothingBasis | null
  windowSeconds?: number | null // used when basis is 'duration'
  alignment?: SmoothingAlignment | null
  display?: SmoothingDisplay | null
  requireFullWindow?: boolean | null
}

export const SmoothingConfigSchema = z
  .object({
    type: z.literal('smoothing'),
    sensor_id: z.string(),
    enabled: z.boolean(),
    window_size: z.number().int().positive(),
    method: SmoothingMethodSchema,
    // Nullish rather than defaulted so charts saved before these existed keep
    // rendering as centered/replace, the behaviour they were saved with.
    basis: SmoothingBasisSchema.nullish(),
    window_seconds: z.number().positive().nullish(), // used when basis is 'duration'
    alignment: SmoothingAlignmentSchema.nullish(),
    display: SmoothingDisplaySchema.nullish(),
    require_full_window: z.boolean().nullish(),
  })
  .transform(
    (schema): SmoothingConfig => ({
      type: schema.type,
      sensorId: schema.sensor_id,
      enabled: schema.enabled,
      windowSize: schema.window_size,
      method: schema.method,
      basis: schema.basis,
      windowSeconds: schema.window_seconds,
      alignment: schema.alignment,
      display: schema.display,
      requireFullWindow: schema.require_full_window,
    })
  )

// ---------- Regression ----------
export const RegressionTypeSchema = z.enum([
  'linear',
  'polynomial',
  'logistic',
  'sinusoidal',
])
export type RegressionType = z.infer<typeof RegressionTypeSchema>

export const RegressionConfigSchema = z
  .object({
    type: z.literal('regression'),
    regression_type: RegressionTypeSchema,
    degree: z.number().int().positive().nullish(), // required for polynomial via refinement below
    id: z.string(),
    enabled: z.boolean(),
    x_variables: z.array(z.string()).min(1),
    y_variable: z.string(),
    end_date: z.string().nullish(),
    min_x: z.number().nullish(),
    max_x: z.number().nullish(),
    should_calculate: z.boolean().nullish(),
  })
  .superRefine((obj, ctx) => {
    if (obj.regression_type === 'polynomial' && obj.degree === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'degree is required for polynomial regression',
        path: ['degree'],
      })
    }
    if (
      obj.min_x !== null ||
      (obj.min_x !== undefined && obj.max_x !== null) ||
      (obj.max_x !== undefined &&
        obj.max_x !== null &&
        obj.min_x !== null &&
        obj.max_x < obj.min_x)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'max_x must be ≥ min_x',
        path: ['max_x'],
      })
    }
  })
  .transform((schema) => ({
    type: schema.type,
    regressionType: schema.regression_type,
    degree: schema.degree,
    id: schema.id,
    enabled: schema.enabled,
    xVariables: schema.x_variables,
    yVariable: schema.y_variable,
    endDate: schema.end_date,
    minX: schema.min_x,
    maxX: schema.max_x,
    shouldCalculate: schema.should_calculate,
  }))

// ---------- Resample (training buckets) ----------
export const ResampleAggregationSchema = z.enum(['avg', 'min', 'max', 'sum'])
export type ResampleAggregation = z.infer<typeof ResampleAggregationSchema>

// A narrowing of ChartTimeUnit, declared here rather than imported because
// geckoChart already imports this module.
export const ResampleIntervalUnitSchema = z.enum([
  'seconds',
  'minutes',
  'hours',
  'days',
])
export type ResampleIntervalUnit = z.infer<typeof ResampleIntervalUnitSchema>

export const ResampleConfigSchema = z
  .object({
    type: z.literal('resample'),
    sensor_id: z.string(),
    enabled: z.boolean(),
    interval: z.number().int().positive(),
    interval_unit: ResampleIntervalUnitSchema,
    aggregation_op: ResampleAggregationSchema,
  })
  .transform((schema) => ({
    type: schema.type,
    sensorId: schema.sensor_id,
    enabled: schema.enabled,
    interval: schema.interval,
    intervalUnit: schema.interval_unit,
    aggregationOp: schema.aggregation_op,
  }))

// ---------- Union ----------
export const DataProcessingConfigSchema = z.discriminatedUnion('type', [
  ThresholdFilterConfigSchema,
  SmoothingConfigSchema,
  RegressionConfigSchema,
  ResampleConfigSchema,
])

// ---------- Inferred Types ----------
export type ThresholdFilterConfig = z.infer<typeof ThresholdFilterConfigSchema>
export type RegressionConfig = z.infer<typeof RegressionConfigSchema>
export type ResampleConfig = z.infer<typeof ResampleConfigSchema>
export type DataProcessingConfig = z.infer<typeof DataProcessingConfigSchema>
